/**
 * Ad-slot registry shared by the client (AdSpace renderer + admin panel) and the
 * server (`/api/ads` size lookup + ad-slot seeding).
 *
 * A slot carries two size guides:
 *
 *  - `size`       — the big-screen size, e.g. "728x90". Sets the height ceiling and
 *                   the box ratio for HTML/JS creatives on desktop.
 *  - `mobileSize` — the small-screen size guide, e.g. "320x100", shown to the admin
 *                   when they upload a mobile creative. It takes over from `size` on
 *                   screens ≤ AD_MOBILE_MAX_WIDTH.
 *
 * Either value can be a preset key ("leaderboard", "rectangle", …) or an explicit
 * `WxH` pixel pair ("728x90").
 */

/**
 * Viewports at or below this width get a slot's small-screen creative and the
 * slot's `mobileSize` guide. 767px = phones + small tablets, i.e. exactly where
 * Tailwind's `md:` is off.
 */
export const AD_MOBILE_MAX_WIDTH = 767;

export type AdSizeSpec = { w: number; h: number; label: string };

export const AD_SIZE_PRESETS: Record<string, AdSizeSpec> = {
  leaderboard: { w: 728, h: 90, label: "728x90" },
  rectangle: { w: 300, h: 250, label: "300x250" },
  skyscraper: { w: 160, h: 600, label: "160x600" },
  "native-tip": { w: 320, h: 160, label: "320x160" },
};

export const AD_SIZE_PRESET_KEYS = [
  "leaderboard",
  "rectangle",
  "skyscraper",
  "native-tip",
] as const;

/**
 * Ready-made sizes offered for a slot's small-screen guide. These are the common
 * IAB mobile formats plus the shapes the built-in slots use.
 */
export const AD_MOBILE_SIZE_PRESETS: { value: string; label: string }[] = [
  { value: "320x50", label: "320x50 — Mobile banner" },
  { value: "320x100", label: "320x100 — Large mobile banner" },
  { value: "300x250", label: "300x250 — Medium rectangle" },
  { value: "336x280", label: "336x280 — Large rectangle" },
  { value: "300x400", label: "300x400 — Portrait unit" },
  { value: "300x600", label: "300x600 — Half page" },
  { value: "320x160", label: "320x160 — Native block" },
  { value: "360x120", label: "360x120 — Compact banner" },
  { value: "360x200", label: "360x200 — Wide mobile card" },
];

export type BuiltinAdSlot = {
  key: string;
  name: string;
  /** Big-screen size guide. */
  size: string;
  /** Small-screen size guide (phones + small tablets). */
  mobileSize: string;
  description: string;
};

/**
 * Slots that always exist. They are seeded into `ad_slots` the first time an
 * admin opens Admin → Ads → Slots, which makes their name/sizes editable.
 */
export const BUILTIN_AD_SLOTS: BuiltinAdSlot[] = [
  {
    key: "leaderboard",
    name: "Global Leaderboard",
    size: "728x90",
    mobileSize: "320x100",
    description: "Wide banner used above/between content on most pages.",
  },
  {
    key: "rectangle",
    name: "Global Rectangle",
    size: "300x250",
    mobileSize: "300x250",
    description: "Square-ish block used in sidebars across the site.",
  },
  {
    key: "skyscraper",
    name: "Global Skyscraper",
    size: "160x600",
    mobileSize: "300x250",
    description: "Tall vertical unit used in sidebars across the site.",
  },
  {
    key: "native-tip",
    name: "Native Tip",
    size: "320x160",
    mobileSize: "320x160",
    description: "Small in-feed/native block.",
  },
  {
    key: "home_leaderboard",
    name: "Premium Global Spotlight — Home top banner",
    size: "1200x160",
    mobileSize: "360x120",
    description: "Full-width homepage banner above the main content grid.",
  },
  {
    key: "home_rectangle",
    name: "Global Premium Spotlight — Home sidebar",
    size: "300x250",
    mobileSize: "300x250",
    description: "Homepage right-rail block under the crypto list.",
  },
  {
    key: "home_skyscraper",
    name: "Premium Vertical Sponsor — Home sidebar bottom",
    size: "300x600",
    mobileSize: "300x400",
    description: "Homepage right-rail tall unit at the bottom of the column.",
  },
  {
    key: "news_leaderboard",
    name: "News — Banner",
    size: "970x250",
    mobileSize: "336x280",
    description:
      "News feed top banner, article in-content banner and the news category/tag/author listings.",
  },
  {
    key: "news_sidebar",
    name: "News Sidebar — Sponsor",
    size: "300x600",
    mobileSize: "300x400",
    description: "Right rail of the news feed and of every article page.",
  },
  {
    key: "analysis_leaderboard",
    name: "Analysis — Top banner",
    size: "970x250",
    mobileSize: "336x280",
    description: "Technical analysis hub, banner above the hero.",
  },
  {
    key: "analysis_infeed",
    name: "Analysis — In-feed banner",
    size: "728x90",
    mobileSize: "320x100",
    description: "Technical analysis hub, on its own row after the fourth listing card.",
  },
  {
    key: "analysis_sidebar",
    name: "Analysis Sidebar — Sponsor",
    size: "300x600",
    mobileSize: "300x400",
    description: "Technical analysis hub, upper right rail.",
  },
  {
    key: "analysis_sidebar_bottom",
    name: "Analysis Sidebar — Bottom",
    size: "300x250",
    mobileSize: "300x250",
    description: "Technical analysis hub, lower right rail.",
  },
  {
    key: "digest_leaderboard",
    name: "News Digest — Top banner",
    size: "970x250",
    mobileSize: "336x280",
    description: "AI News Digest, banner directly under the page header.",
  },
  {
    key: "digest_sidebar",
    name: "News Digest Sidebar — Sponsor",
    size: "300x600",
    mobileSize: "300x400",
    description: "AI News Digest, right rail above the tools cards.",
  },
  {
    key: "digest_sidebar_bottom",
    name: "News Digest Sidebar — Bottom",
    size: "300x250",
    mobileSize: "300x250",
    description: "AI News Digest, lower right rail, under the tools cards.",
  },
  {
    key: "digest_footer",
    name: "News Digest — Bottom banner",
    size: "970x250",
    mobileSize: "336x280",
    description: "AI News Digest, closing banner above the footer.",
  },
  {
    key: "complaints-sidebar-top",
    name: "Complaints Sidebar — Top",
    size: "300x250",
    mobileSize: "300x250",
    description: "Complaint detail pages, upper right rail.",
  },
  {
    key: "complaints-sidebar-bottom",
    name: "Complaints Sidebar — Bottom",
    size: "300x600",
    mobileSize: "300x400",
    description: "Complaint detail pages, lower right rail.",
  },
];

const BUILTIN_SLOT_BY_KEY: Record<string, BuiltinAdSlot> = BUILTIN_AD_SLOTS.reduce(
  (acc, slot) => {
    acc[slot.key] = slot;
    return acc;
  },
  {} as Record<string, BuiltinAdSlot>,
);

export function getBuiltinAdSlot(key?: string | null): BuiltinAdSlot | null {
  const k = String(key ?? "").trim();
  if (!k) return null;
  return BUILTIN_SLOT_BY_KEY[k] || null;
}

/** Parse an explicit `WxH` string (accepts `x`, `X`, `×` and spaces). */
export function parseAdSize(raw?: string | null): AdSizeSpec | null {
  const m = String(raw ?? "")
    .trim()
    .toLowerCase()
    .match(/^(\d{2,5})\s*[x×*]\s*(\d{2,5})$/);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { w, h, label: `${w}x${h}` };
}

/**
 * Resolve anything a slot's `size` column can hold (preset key, `WxH`, or a
 * built-in slot key such as `home_leaderboard`) into concrete pixel dimensions.
 */
export function resolveAdSize(raw?: string | null): AdSizeSpec | null {
  const value = String(raw ?? "").trim();
  if (!value) return null;

  const preset = AD_SIZE_PRESETS[value.toLowerCase()];
  if (preset) return preset;

  const parsed = parseAdSize(value);
  if (parsed) return parsed;

  const builtin = getBuiltinAdSlot(value);
  if (builtin) return resolveAdSize(builtin.size);

  return null;
}

/** The declared big-screen size for a slot key, from the built-in registry only. */
export function builtinAdSlotSize(key?: string | null): AdSizeSpec | null {
  const builtin = getBuiltinAdSlot(key);
  return builtin ? resolveAdSize(builtin.size) : null;
}

/** The small-screen size guide for a slot key, from the built-in registry only. */
export function builtinAdSlotMobileSize(key?: string | null): AdSizeSpec | null {
  const builtin = getBuiltinAdSlot(key);
  return builtin ? resolveAdSize(builtin.mobileSize) : null;
}

/**
 * Effective small-screen size for a slot: whatever the admin saved on the slot
 * row, otherwise the built-in guide. `undefined`/`""` ⇒ fall back.
 */
export function resolveSlotMobileSize(
  slotMobileSize: string | null | undefined,
  slotKey?: string | null,
): AdSizeSpec | null {
  return resolveAdSize(slotMobileSize) || builtinAdSlotMobileSize(slotKey);
}

export function adSizeLabel(raw?: string | null): string {
  const spec = resolveAdSize(raw);
  return spec ? spec.label : "";
}
